export function minLockedCount<T>(items: T[], isOccupied: (item: T, index: number) => boolean): number {
  let min = 0
  for (let i = 0; i < items.length; i++) {
    if (isOccupied(items[i], i)) min = i + 1
  }
  return min
}

export type ResizeArrayOptions = {
  /** Custom message when shrinking below occupied slots (tabs / rows). */
  blockedShrinkMessage?: (locked: number) => string
}

export function defaultTabBlockedMessage(locked: number): string {
  const n = locked
  return `You have ${n} tab${n === 1 ? '' : 's'} with data. Delete a tab (or clear its fields) before reducing the count below ${n}.`
}

export function pipeRowBlockedMessage(locked: number): string {
  const n = locked
  return `You have ${n} pipe row${n === 1 ? '' : 's'} with data. Remove a row or clear its fields before reducing below ${n}.`
}

export function weldTabBlockedMessage(locked: number): string {
  const n = locked
  return `You have ${n} weld${n === 1 ? '' : 's'} with data. Delete a weld tab (or clear its data) before reducing below ${n}.`
}

export function pipeSetBlockedMessage(locked: number): string {
  const n = locked
  return `You have ${n} pipe size set${n === 1 ? '' : 's'} with data. Delete a set or clear its fields before reducing below ${n}.`
}

export function measurementColumnBlockedMessage(locked: number): string {
  const n = locked
  return `Measurements exist for ${n} column${n === 1 ? '' : 's'}. Clear those cells or reduce columns after clearing data below ${n}.`
}

export type ResizeArrayResult<T> = { ok: true; items: T[] } | { ok: false; error: string }

export function resizeArrayByCount<T>(
  items: T[],
  targetCount: number,
  createEmpty: () => T,
  isOccupied: (item: T, index: number) => boolean,
  options?: ResizeArrayOptions
): ResizeArrayResult<T> {
  const locked = minLockedCount(items, isOccupied)
  if (targetCount < locked) {
    const msg = options?.blockedShrinkMessage
      ? options.blockedShrinkMessage(locked)
      : defaultTabBlockedMessage(locked)
    return { ok: false, error: msg }
  }
  if (targetCount === items.length) return { ok: true, items }
  if (targetCount < items.length) {
    return { ok: true, items: items.slice(0, targetCount) }
  }
  const next = [...items]
  while (next.length < targetCount) {
    next.push(createEmpty())
  }
  return { ok: true, items: next }
}
